import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";

export interface MultiSelectOption { value: string; label: string; hint?: string }

interface MultiSelectDropdownProps {
  value: string[];
  options: ReadonlyArray<MultiSelectOption>;
  onChange: (value: string[]) => void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Force the menu to open downward even when space below looks tight. */
  dropDown?: boolean;
}

/**
 * Portal-based multi-select: menu renders in <body>, so it can never be
 * clipped by scroll containers. Stays open while ticking; closes on outside
 * click or Escape. Mirrors Dropdown's positioning, including the flip-up.
 */
export function MultiSelectDropdown({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = "Select…",
  className,
  disabled,
  dropDown = false,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});

  const isSearchable = options.length > 8;
  const filtered = isSearchable && query
    ? options.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(query.toLowerCase()))
    : options;
  const selected = new Set(value);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAX_H = 300;
    const below = window.innerHeight - r.bottom;
    const up = !dropDown && below < MAX_H + 12 && r.top > below;
    const w = Math.max(r.width, 200);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setStyle({
      position: "fixed",
      left,
      width: w,
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      maxHeight: Math.min(MAX_H, (up ? r.top : below) - 12),
      zIndex: 9999,
    });
  }, [dropDown]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    place();
    const raf = requestAnimationFrame(() => { place(); requestAnimationFrame(place); });
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open ]);

  const toggle = (optValue: string) => {
    onChange(selected.has(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
  };

  const summary = value.length === 0
    ? placeholder
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label ?? value[0])
      : `${value.length} selected`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border border-line bg-soft px-3 py-2 text-sm text-ink transition-colors hover:bg-line/60 focus:outline-none focus:ring-2 focus:ring-brand/30",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span className={cn("truncate", value.length === 0 && "text-ink-3")}>{summary}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-multiselectable
          style={style}
          className="flex flex-col overflow-hidden rounded-xl border border-line bg-elevated shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-xs font-semibold text-ink-3">{value.length} selected</span>
            {value.length > 0 && (
              <button type="button" onClick={() => onChange([])} className="text-xs font-semibold text-brand hover:underline">
                Clear all
              </button>
            )}
          </div>
          {isSearchable && (
            <div className="relative border-b border-line">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter…"
                className="w-full bg-elevated py-2 pl-9 pr-3 text-sm text-ink outline-none placeholder:text-ink-3"
              />
            </div>
          )}
          <ul className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-3">No matches.</li>
            )}
            {filtered.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(opt.value)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-soft"
                  >
                    <span className={cn(
                      "relative h-5 w-9 shrink-0 rounded-full transition-colors",
                      checked ? "bg-accent-500" : "bg-line",
                    )}>
                      <span className={cn(
                        "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
                        checked ? "left-[18px]" : "left-0.5",
                      )} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-ink-2">{opt.label}</span>
                      {opt.hint && <span className="block truncate text-xs text-ink-3">{opt.hint}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
